/**
 * Denial-time merge-suggestion store.
 *
 * Drives the app-wide `AccountMergeHost` (mounted in RootLayout). Set from
 * the API client's 403 interceptor — a zustand store so it is reachable from
 * outside React, same pattern as `useFailureStore`.
 *
 * Only the DENIAL channel lives here. The PROACTIVE channel (login /
 * auth-me refresh) needs no store at all: `user.mergeSuggestion` on the
 * auth store is already reactive server state, and the host derives the
 * proactive dialog directly from its presence (auth/me only ever carries
 * non-ignored suggestions, and the dialog is a forced binary choice, so
 * there is no dismissed-state to track).
 */
import { create } from 'zustand';
import { registerStoreReset } from './registry';
import type { DenialEnvelope } from '@/api/mergeDenial';

/**
 * sessionStorage key for suggestions this session has finished with (#223
 * defect B/C). Session-scoped on purpose: the backend re-delivers ignored
 * suggestions at denial time and when the viewer's inbox resolves to zero
 * contacts, which is the point — but re-delivering the SAME id into the same
 * session, after the user just ignored it or after it 404ed, is the loop that
 * fired nine `/users/merge/{id}/ignore/` 404s in 30 seconds.
 *
 * sessionStorage rather than in-memory so a reload inside the tab does not
 * re-open it; sessionStorage rather than localStorage so a genuinely new
 * session still sees it.
 */
const DISMISSED_STORAGE_KEY = 'ving.dismissedMergeSuggestions';

function loadDismissed(): number[] {
  try {
    const raw = sessionStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    // Private mode / disabled storage / corrupt value: an empty list is the
    // pre-#223 behaviour, never a crash.
    return [];
  }
}

function saveDismissed(ids: number[]): void {
  try {
    sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Non-fatal — the in-memory list still suppresses the re-pop this session.
  }
}

interface MergeSuggestionState {
  denial: DenialEnvelope | null;
  /**
   * Proactive suggestions this session has finished with: ignored, or gone
   * (404). The host will not re-open these even if /auth/me keeps sending
   * them.
   */
  dismissedIds: number[];
  /** Record a suggestion as finished with for the rest of this session. */
  dismissSuggestion: (id: number) => void;
  /**
   * Record a denial-time suggestion. No-ops when the same suggestion id is
   * already showing — a denied page typically fires several requests that
   * all 403 with the identical envelope, and replacing state per response
   * would re-render (and could reset) the open dialog.
   */
  showDenial: (envelope: DenialEnvelope) => void;
  clearDenial: () => void;
}

export const useMergeSuggestionStore = create<MergeSuggestionState>((set, get) => ({
  denial: null,
  dismissedIds: loadDismissed(),
  dismissSuggestion: (id) => {
    const current = get().dismissedIds;
    if (current.includes(id)) return;
    const next = [...current, id];
    saveDismissed(next);
    set({ dismissedIds: next });
  },
  showDenial: (envelope) => {
    const current = get().denial;
    if (current && current.suggestion.id === envelope.suggestion.id) return;
    set({ denial: envelope });
  },
  clearDenial: () => set({ denial: null }),
}));

// Sign-out: everything goes. A user switch must not close the dialog the person
// is in the middle of (AccountMergeDialog's own merge can switch the id, #1336).
registerStoreReset(() => {
  try {
    sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    // ignored — see saveDismissed
  }
  useMergeSuggestionStore.setState({ denial: null, dismissedIds: [] });
}, { onUserSwitch: false });

// Switch: only the dismissed list belongs to the folded login; `denial` stays.
registerStoreReset(() => {
  try {
    sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
  } catch {
    // ignored — see saveDismissed
  }
  useMergeSuggestionStore.setState({ dismissedIds: [] });
}, { onUserSwitch: true });
